import type { FormStore } from "shared/react";
import type { EarnedData, RewardData, RewardFormData, RewardType } from "shared/schema";
import type { AsyncValueCallback } from "shelving";
import { getRandom } from "shelving";
import { useStore } from "shelving/react";
import { CREATE_REWARD } from "shared/api";
import { Field, Form, HueField, Loader, MediaField, SubmitButton, TextAreaField, TextField, useDialog, useFormContext } from "shared/react";
import { REWARD_FORM_SCHEMA } from "shared/schema";
import { RawRewardCard } from "../reward/RewardCard.js";
import { callAPI } from "../util/api.js";
import { uploadMediaFile } from "../util/media.js";
import { CreatorField } from "./CreatorField.js";
import { CreatorPicker } from "./CreatorPicker.js";

export function RewardForm({ type, data, onSubmit, children }: { type: RewardType; data?: Partial<RewardData> | undefined; onSubmit: AsyncValueCallback<RewardFormData>; children: React.ReactNode }): React.ReactElement {
	return (
		<Form schema={REWARD_FORM_SCHEMA} onSubmit={onSubmit} data={data}>
			<RewardFormCard type={type} />
			<Field title="Image">
				<MediaField name="image" uploader={uploadMediaFile} />
				<HueField name="hue" />
			</Field>
			<Field title="Details">
				<TextField name="name" />
				<TextAreaField name="about" />
			</Field>
			<TextAreaField name="secret" />
			<CreatorField name="creator" />
			{children}
		</Form>
	);
}

function RewardFormCard({ type }: { type: RewardType }) {
	const form = useStore(useFormContext<RewardData>());
	const showDialog = useDialog();
	return (
		<div
			onClick={e => {
				e.preventDefault();
				e.stopPropagation();
				if (e.target instanceof Element) {
					if (e.target.closest(".button")) showDialog(<RewardCreatorModal form={form} />);
				}
				return false;
			}}
		>
			<RawRewardCard reward={{ ...(form.data as RewardData), type }} earned={{ count: getRandom(2, 19) } as EarnedData} />
		</div>
	);
}

function RewardCreatorModal({ form }: { form: FormStore<RewardData> }): React.ReactElement {
	return (
		<article className="modal">
			<Loader>
				<CreatorPicker onPick={x => form.setProp("creator", x)} creator={form.value.creator} />
			</Loader>
		</article>
	);
}

export function RewardCreateForm({ type, onReward, data }: { type: RewardType; onReward: AsyncValueCallback<string>; data: Partial<RewardData> }) {
	return (
		<RewardForm type={type} data={{ hue: getRandom(0, 360), ...data }} onSubmit={next => callAPI(CREATE_REWARD, { ...next, type }).then(onReward)}>
			<SubmitButton>{`Create ${type}`}</SubmitButton>
		</RewardForm>
	);
}
